import { useEffect, useState } from "react";
import { toast } from "sonner";
import { BarChart, Bar, ResponsiveContainer, Tooltip, XAxis, YAxis, LineChart, Line, PieChart, Pie, Cell } from "recharts";
import { AlertTriangle, Flame, MapPin } from "lucide-react";
import { api } from "@/lib/apiClient";

const COLORS = ["#FFC93C", "#FF4F5A", "#2B2B2B", "#7DD3C0", "#A07F58", "#E27D9C"];

const STATUSES = ["new", "in_progress", "fulfilled", "expansion"];

export default function AdminDemand() {
  const [data, setData] = useState(null);
  const [requests, setRequests] = useState([]);

  const load = async () => {
    const [d, r] = await Promise.all([
      api.get("/admin/demand"),
      api.get("/admin/stock-requests"),
    ]);
    setData(d.data);
    setRequests(r.data || []);
  };
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    await api.put(`/admin/stock-requests/${id}/status`, { status });
    toast.success(`Set ${status}`); load();
  };

  if (!data) return <div className="text-brand-charcoal/60">Loading demand…</div>;

  return (
    <div data-testid="admin-demand" className="space-y-10">
      <header>
        <div className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Demand & Stock Requests</div>
        <h1 className="editorial-h1 text-5xl mt-2">{data.total} requests in the pipeline.</h1>
      </header>

      {data.high_demand_areas.length > 0 && (
        <section className="card-elevated p-6 border-l-4 border-l-brand-red">
          <div className="flex items-center gap-2 text-brand-red"><Flame size={16} /><div className="text-[11px] uppercase tracking-[0.22em]">High Demand Areas — Expansion Opportunity</div></div>
          <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {data.high_demand_areas.map((a) => (
              <div key={a._id} className="card-soft p-4">
                <div className="flex items-center gap-2 text-xs text-brand-charcoal/55"><MapPin size={12} /> Location</div>
                <div className="font-display text-2xl mt-1">{a._id}</div>
                <div className="text-xs text-brand-red mt-1 font-medium">{a.count} active requests</div>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="grid lg:grid-cols-3 gap-6">
        <div className="card-elevated p-6 lg:col-span-2">
          <div className="font-display text-2xl mb-4">Request Trend (30 days)</div>
          <div className="h-64">
            <ResponsiveContainer>
              <LineChart data={data.trend || []}>
                <XAxis dataKey="day" stroke="#736F6A" fontSize={10} />
                <YAxis stroke="#736F6A" fontSize={10} allowDecimals={false} />
                <Tooltip />
                <Line type="monotone" dataKey="count" stroke="#FF4F5A" strokeWidth={2} dot={{ fill: "#FFC93C" }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="card-elevated p-6">
          <div className="font-display text-2xl mb-4">By Category</div>
          <div className="h-64">
            <ResponsiveContainer>
              <PieChart>
                <Pie data={(data.by_category || []).map((d) => ({ name: d._id, value: d.count }))} dataKey="value" innerRadius={40} outerRadius={80}>
                  {(data.by_category || []).map((_, i) => (<Cell key={i} fill={COLORS[i % COLORS.length]} />))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
            {!data.by_category?.length && <div className="text-sm text-brand-charcoal/60 text-center -mt-32">No requests yet.</div>}
          </div>
        </div>
      </section>

      <section className="grid lg:grid-cols-2 gap-6">
        <div className="card-elevated p-6">
          <div className="font-display text-2xl mb-4">Top Outlets</div>
          <div className="h-64">
            <ResponsiveContainer>
              <BarChart data={(data.by_outlet || []).map((d) => ({ name: d._id, count: d.count }))} layout="vertical">
                <XAxis type="number" stroke="#736F6A" fontSize={10} />
                <YAxis dataKey="name" type="category" width={120} stroke="#736F6A" fontSize={10} />
                <Tooltip />
                <Bar dataKey="count" fill="#FFC93C" radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="card-elevated p-6">
          <div className="font-display text-2xl mb-4">Top Locations</div>
          <div className="h-64">
            <ResponsiveContainer>
              <BarChart data={(data.by_location || []).map((d) => ({ name: d._id, count: d.count }))} layout="vertical">
                <XAxis type="number" stroke="#736F6A" fontSize={10} />
                <YAxis dataKey="name" type="category" width={120} stroke="#736F6A" fontSize={10} />
                <Tooltip />
                <Bar dataKey="count" fill="#FF4F5A" radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </section>

      <section>
        <div className="font-display text-2xl mb-4">All Requests</div>
        <div className="card-elevated overflow-x-auto">
          <table className="w-full text-sm" data-testid="demand-table">
            <thead className="text-left text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">
              <tr className="border-b border-brand-line">
                <th className="p-4">Customer</th><th className="p-4">Outlet</th><th className="p-4">Location</th>
                <th className="p-4">Category</th><th className="p-4">Qty</th><th className="p-4">Status</th><th className="p-4">When</th>
              </tr>
            </thead>
            <tbody>
              {requests.map((r) => (
                <tr key={r.id} className="border-b border-brand-line last:border-0" data-testid={`req-${r.id}`}>
                  <td className="p-4"><div className="font-medium">{r.name}</div><div className="text-xs text-brand-charcoal/55">{r.phone}</div></td>
                  <td className="p-4">{r.outlet_name}</td>
                  <td className="p-4">{r.outlet_location}</td>
                  <td className="p-4 text-xs uppercase tracking-[0.18em] text-brand-charcoal/65">{r.product_category || "any"}</td>
                  <td className="p-4 text-brand-charcoal/70">{r.quantity_needed || "—"}</td>
                  <td className="p-4">
                    <select data-testid={`req-status-${r.id}`} className="text-xs border border-brand-line rounded-full px-2 py-1 bg-white" value={r.status || "new"} onChange={(e) => setStatus(r.id, e.target.value)}>
                      {STATUSES.map((s) => (<option key={s} value={s}>{s}</option>))}
                    </select>
                  </td>
                  <td className="p-4 text-xs text-brand-charcoal/55">{(r.created_at || "").slice(0, 10)}</td>
                </tr>
              ))}
              {requests.length === 0 && (<tr><td colSpan="7" className="p-8 text-center text-brand-charcoal/60">No stock requests yet.</td></tr>)}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
